// AdminMerchants.jsx
import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import { Search, Building2, CheckCircle, Ban, Plus, X } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export function AdminMerchants() {
  const [merchants, setMerchants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => { fetchMerchants(); }, [search, status, page]);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getMerchants({ search, status, page, limit: 20 });
      setMerchants(data.data.merchants);
      setTotal(data.data.total);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const toggleStatus = async (merchant) => {
    const newStatus = merchant.status === 'active' ? 'blocked' : 'active';
    try {
      await adminAPI.updateMerchantStatus(merchant._id, { status: newStatus });
      setMerchants(prev => prev.map(m => m._id === merchant._id ? { ...m, status: newStatus } : m));
      toast.success(`Merchant ${newStatus}`);
    } catch (_) {}
  };

  const statusBadge = (s) => {
    const map = { active: 'badge-green', blocked: 'badge-red', pending: 'badge-yellow', suspended: 'badge-yellow' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const handleMerchantCreated = () => {
    setShowCreateModal(false);
    fetchMerchants();
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Merchants</h1><p className="page-subtitle">{total} total merchants</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={16} /> Create Merchant
        </button>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: 300 }}>
            <Search size={14} />
            <input className="form-input" style={{ paddingLeft: 34 }} placeholder="Search merchants…" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-input" style={{ width: 160 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="card" style={{ padding: 0 }}>
          {loading ? <div className="flex-center" style={{ padding: 60 }}><div className="spinner" /></div> : (
            <div className="table-container">
              <table>
                <thead><tr><th>Business</th><th>Owner</th><th>Plan</th><th>Status</th><th>Tokens (in / out)</th><th>AI Cost</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {merchants.map(m => {
                    const tu = m.tokenUsage || { inputTokens: 0, outputTokens: 0, totalCost: 0 };
                    return (
                    <tr key={m._id}>
                      <td><div style={{ fontWeight: 600 }}>{m.businessName}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.email}</div></td>
                      <td><div style={{ fontSize: '0.8rem' }}>{m.ownerId?.name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.ownerId?.email}</div></td>
                      <td><span className="badge badge-mauve" style={{ textTransform: 'capitalize' }}>{m.subscription?.plan || 'free'}</span></td>
                      <td>{statusBadge(m.status)}</td>
                      <td>
                        <div style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)' }}>
                          {Number(tu.inputTokens || 0).toLocaleString()} / {Number(tu.outputTokens || 0).toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {Number(tu.totalTokens || 0).toLocaleString()} total
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>${Number(tu.totalCost || 0).toFixed(4)}</span>
                      </td>
                      <td><span style={{ fontSize: '0.8rem' }}>{format(new Date(m.createdAt), 'MMM d, yyyy')}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link to={`/admin/merchants/${m._id}`} className="btn btn-secondary btn-sm">View</Link>
                          <button className={`btn btn-sm ${m.status === 'active' ? 'btn-danger' : 'btn-primary'}`}
                            onClick={() => toggleStatus(m)}>
                            {m.status === 'active' ? <><Ban size={12} /> Block</> : <><CheckCircle size={12} /> Unblock</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {total > 20 && (
          <div className="flex-between" style={{ marginTop: 14 }}>
            <span className="text-muted">Page {page} of {Math.ceil(total / 20)}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateMerchantModal onClose={() => setShowCreateModal(false)} onCreated={handleMerchantCreated} />
      )}
    </div>
  );
}

const BUSINESS_TYPES = [
  { value: 'order', label: 'Order / E-Commerce', desc: 'Restaurants, retail, online shops' },
  { value: 'appointment', label: 'Appointment / Booking', desc: 'Salons, clinics, consultants' },
  { value: 'lead', label: 'Lead / Inquiry', desc: 'Real estate, insurance, agencies' },
  { value: 'support', label: 'Support / Helpdesk', desc: 'SaaS, tech support, services' },
  { value: 'catalog', label: 'Catalog / Info', desc: 'Information only, no transactions' },
];

function CreateMerchantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', businessName: '', phone: '', plan: '', businessType: 'catalog',
  });
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    adminAPI.getPlans().then(({ data }) => {
      const activePlans = (data.data || []).filter(p => p.isActive);
      setPlans(activePlans);
      if (activePlans.length > 0 && !form.plan) setForm(prev => ({ ...prev, plan: activePlans[0].slug }));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setSubmitting(true);
    try {
      await adminAPI.createMerchant(form);
      toast.success('Merchant created successfully');
      onCreated();
    } catch (_) {}
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h3>Create Merchant</h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label className="form-label">Owner Name *</label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} placeholder="merchant@example.com" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label className="form-label">Password *</label>
                <input className="form-input" name="password" type="password" value={form.password} onChange={handleChange} placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input className="form-input" name="phone" value={form.phone} onChange={handleChange} placeholder="+1234567890" />
              </div>
            </div>
            <div>
              <label className="form-label">Business Name</label>
              <input className="form-input" name="businessName" value={form.businessName} onChange={handleChange} placeholder="My Business" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label className="form-label">Plan</label>
                <select className="form-input" name="plan" value={form.plan} onChange={handleChange}>
                  {plans.length === 0 && <option value="">Loading plans...</option>}
                  {plans.map(p => <option key={p.slug} value={p.slug}>{p.name}{p.price?.monthly > 0 ? ` — $${(p.price.monthly / 100).toFixed(0)}/mo` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Business Type</label>
                <select className="form-input" name="businessType" value={form.businessType} onChange={handleChange}>
                  {BUSINESS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {BUSINESS_TYPES.find(t => t.value === form.businessType)?.desc}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Merchant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminMerchants;
